import type { StreamStatus } from '../lib/stream.js';

export interface ConnectionBadgeProps {
  status: StreamStatus;
}

/**
 * Whether the list can be trusted to be current.
 *
 * The distinction that matters is "reconnecting" versus "failed": the first
 * means wait, the second means the list is frozen and no amount of waiting will
 * unfreeze it. Showing one dot for both would leave people staring at a stale
 * inspector believing nothing had arrived.
 */
export function ConnectionBadge({ status }: ConnectionBadgeProps) {
  const { dot, label, tone } = describe(status);

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${tone}`}
      // Polite, not assertive: a flapping connection should not interrupt a
      // screen-reader user mid-sentence, but it should eventually be announced.
      role="status"
      aria-live="polite"
    >
      <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" />
      {label}
    </span>
  );
}

function describe(status: StreamStatus): { dot: string; label: string; tone: string } {
  switch (status.kind) {
    case 'open':
      return {
        dot: 'bg-emerald-400',
        label: 'En vivo',
        tone: 'bg-emerald-500/10 text-emerald-300',
      };
    case 'connecting':
      return {
        dot: 'bg-slate-400 animate-pulse',
        label: 'Conectando…',
        tone: 'bg-slate-500/10 text-slate-300',
      };
    case 'reconnecting':
      return {
        dot: 'bg-amber-400 animate-pulse',
        label: `Reconectando (${status.attempt})`,
        tone: 'bg-amber-500/10 text-amber-300',
      };
    case 'failed':
      return {
        dot: 'bg-rose-500',
        label: 'Sin conexión',
        tone: 'bg-rose-500/10 text-rose-300',
      };
  }
}
